import api from 'api/apiClient';
import dayjs from 'dayjs';
import { ensureFileId } from 'modules/shared/service';
import { extractMonth, extractYear } from 'modules/shared/utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export const getOfferContext = async (id) => {
  if (!id) throw new Error('Candidate id is required');

  const raw = await unwrap(await api.get(`/api/candidate/offer-context/${id}`));

  return {
    id: raw?.id ?? null,
    currentInterviewRound: raw?.current_interview_round,
    offers: raw?.offers
      ? raw.offers.map((o) => ({
          salaryFinalized: o.salary_finalized,
          offerLetter: {
            status: o.offer_letter.status,
            id: o.offer_letter.id,
            name: o.offer_letter.name,
            url: o.offer_letter.url,
            sendDate: o.offer_letter.send_date,
            acceptedDate: o.offer_letter.accepted_date,
            rejectedDate: o.offer_letter.rejected_date,
          },
        }))
      : [],
    contract: raw?.contract,
    personalData: raw?.personal_data
      ? {
          status: raw.personal_data?.status,
          name: raw.personal_data?.name,
          contact: raw.personal_data?.contact_number,
          email: raw.personal_data?.email,
          dateOfBirth: raw.personal_data?.date_of_birth
            ? dayjs(raw.personal_data.date_of_birth).isValid()
              ? dayjs(raw.personal_data.date_of_birth).format('YYYY-MM-DD')
              : null
            : null,
          gender: raw.personal_data?.gender,
          photo: raw.personal_data?.photo,
          residentialAddress: raw.personal_data?.residential_address,
          permanentAddress: raw.personal_data?.permanent_address,
          nationality: raw.personal_data?.nationality,
          medicalCondition: raw.personal_data?.medical_condition,
          documents: raw.personal_data?.documents,
          professionalExperience: (raw.personal_data?.professional_experience || []).map((exp) => ({
            title: exp.title,
            company: exp.company,
            employmentType: exp.employment_type,
            locationType: exp.location_type,
            location: exp.location,
            isCurrent: exp.is_current,
            startDate: dayjs(exp.start_date).isValid()
              ? dayjs(exp.start_date).format('YYYY-MM-DD')
              : null,
            endDate: dayjs(exp.end_date).isValid()
              ? dayjs(exp.end_date).format('YYYY-MM-DD')
              : null,
            description: exp.description,
          })),
        }
      : {},
  };
};

export const updateOfferAction = async (id, data) => {
  if (!id) throw new Error('Candidate id and data is required');
  await unwrap(await api.put(`/api/candidate/offer-action/${id}`, data));
};

export const submitCandidateDataEntry = async (id, data) => {
  if (!id || !data) throw new Error('Candidate id and data is required');
  const { personalData, documents, professionalExperience } = data || {};

  const documentEntries = (
    await Promise.all(
      Object.entries(documents)
        .filter(([, wrapper]) => {
          if (!wrapper) return false;

          if ((wrapper as any).backendFile?.id) return true;

          if ((wrapper as any).file instanceof File) return true;

          return false;
        })
        .map(async ([name, wrapper]) => {
          const id = await ensureFileId((wrapper as any).backendFile ?? wrapper as any);

          if (!id) return null;

          return {
            name,
            id,
            uploaded_by: 'candidate',
            uploaded_date: new Date().toISOString(),
          };
        }),
    )
  ).filter(Boolean);

  const photoId = await ensureFileId(personalData.photo);

  const payload = {
    status: 'submitted',
    name: personalData.name,
    date_of_birth: personalData.dateOfBirth,
    gender: personalData.gender,
    contact_number: personalData.contact,
    email: personalData.email,
    residential_address: personalData.residentialAddress,
    permanent_address: personalData.permanentAddress,
    nationality: personalData.nationality,
    medical_condition: personalData.medicalCondition,
    photo: photoId,
    documents: documentEntries,
    professional_experience: professionalExperience
      .filter((exp) => !Object.values(exp).every((v) => !v))
      .map((exp) => {
        const isEmpty = Object.values(exp).every((v) => !v);
        const startMonth = extractMonth(exp.startMonth);
        const startYear = extractYear(exp.startYear);

        const endMonth = extractMonth(exp.endMonth);
        const endYear = extractYear(exp.endYear);

        return isEmpty
          ? null
          : {
              title: exp.title,
              company: exp.company,
              employment_type: exp.employmentType,
              location_type: exp.locationType,
              location: exp.location,
              is_current: exp.isCurrent,
              start_date:
                startMonth && startYear
                  ? `${startYear}-${String(startMonth).padStart(2, '0')}-01`
                  : null,

              end_date: exp.isCurrent
                ? null
                : endMonth && endYear
                  ? `${endYear}-${String(endMonth).padStart(2, '0')}-01`
                  : null,
              description: exp.description,
            };
      }),
  };

  await unwrap(await api.put(`/api/candidate/personal-data/${id}`, payload));
};
